import React, { useEffect, useState } from "react";

import Options, { SecuritiesIdentifiers } from "./components/options";

import { AccordionHeader, AccordionItem, AccordionPanel, Body1, Divider } from "@fluentui/react-components";
import { DocumentTable20Filled } from "@fluentui/react-icons";
import useSecuritiesStyles from "./securities.styles";

import { AccordionValue } from "../../config";
import { deleteSecurityProducer, insertSecurityProducer } from "../../../../service/producerServices";

import { useSelector } from "react-redux";
import { selectSecurities } from "../../store";
import { useAppDispatch } from "../../../../store";
import {
  removeSecurityFromStagingAndSecuritiesValues,
  updateRowsInStagingAreaAfterDeletingSecurities,
} from "../../../../service/store";
import { selectIsConnected } from "../../../../taskpane/components/statusBar/store";
import { filterSecurities } from "./utils";
import SecuritiesList from "./components/securitiesList";
import PostHogService from "../../../../service/PostHogService";

const SECURITY_LABELS = {
  [SecuritiesIdentifiers.ISIN]: 0,
  [SecuritiesIdentifiers.SecurityName]: 1,
  [SecuritiesIdentifiers.Alias]: 2,
};

export interface SecuritiesInList {
  [key: string]: {
    isCheck: boolean;
    name: string;
    isin: string;
    alias: string;
    security_type: string;
    maturity_date: string;
  };
}

const Securities = () => {
  const [securitiesInList, setSecuritiesInList] = useState<SecuritiesInList>({});
  const [securitiesInListFiltered, setSecuritiesInListFiltered] = useState<SecuritiesInList>({});
  const [searchCriteria, setSearchCriteria] = useState("");
  const [securitiesIdentifiers, setSecuritiesIdentifiers] = useState<SecuritiesIdentifiers>(SecuritiesIdentifiers.ISIN);

  const securitiesFromState = useSelector(selectSecurities);
  const isConnectionEstablished = useSelector(selectIsConnected);

  const styles = useSecuritiesStyles();

  const dispatch = useAppDispatch();

  const handleFilterSecurities = (filter: string) => {
    if (!filter) return setSecuritiesInListFiltered(securitiesInList);

    const securitiesFiltered = filterSecurities({ securities: securitiesInList, filter });

    setSecuritiesInListFiltered(securitiesFiltered);
  };

  const handleSecuritiesIdentifiersChange = (securitiesIdentifiers: SecuritiesIdentifiers) =>
    setSecuritiesIdentifiers(securitiesIdentifiers);

  const deleteDataProducers = async () => {
    let isinsToDelete: string[] = [];
    let deletedRows: number[] | null = null;

    Object.keys(securitiesInList).forEach((isin) => {
      if (securitiesInList[isin].isCheck) {
        isinsToDelete.push(isin);
      }
    });

    await Excel.run(async (context) => {
      deletedRows = await deleteSecurityProducer(context, isinsToDelete);
      await context.sync();
    });

    isinsToDelete.forEach((isin) => {
      dispatch(removeSecurityFromStagingAndSecuritiesValues({ isin }));
    });

    const securitiesInListCopy = { ...securitiesInList };

    isinsToDelete.forEach((isin) => {
      securitiesInListCopy[isin].isCheck = false;
    });

    isinsToDelete = [];

    setSecuritiesInList(securitiesInListCopy);
    dispatch(updateRowsInStagingAreaAfterDeletingSecurities({ deletedRows }));

    PostHogService.capture('data_producer_deleted', {
      isins: isinsToDelete,
      total_deleted: isinsToDelete.length,
      component: 'securities_list',
    });
  };

  const addDataProducers = () => {
    const isinsToAdd: string[] = [];

    Object.keys(securitiesInList).forEach((isin) => {
      if (securitiesInList[isin].isCheck) {
        isinsToAdd.push(isin);
        securitiesInList[isin].isCheck = false;
      }
    });

    Excel.run(async (context) => {
      await insertSecurityProducer(context, isinsToAdd);
      await context.sync();
    });

    const securitiesInListCopy = { ...securitiesInList };

    isinsToAdd.forEach((isin) => {
      securitiesInListCopy[isin].isCheck = false;
    });

    setSecuritiesInList(securitiesInListCopy);

    PostHogService.capture('data_producer_added', {
      isins: isinsToAdd,
      total_added: isinsToAdd.length,
      component: 'securities_list',
    });
  };

  const handleSearch = (value: string) => {
    handleFilterSecurities(value);
    setSearchCriteria(value);

    PostHogService.capture('security_search', {
      search_query: value,
      search_length: value.length,
      has_results: Object.keys(securitiesInListFiltered).length > 0,
      total_securities: Object.keys(securitiesInList).length,
      results_count: Object.keys(securitiesInListFiltered).length,
      search_type: securitiesIdentifiers,
      component: 'securities_list',
    });
  };

  useEffect(() => {
    const securitiesIsins = securitiesFromState.map(
      (security) => security[SECURITY_LABELS[SecuritiesIdentifiers.ISIN]]
    );

    const securitiesFormatted = securitiesIsins.reduce((acc, isin) => {
      const security = securitiesFromState.find((security) => security[0] === isin);
      if (!security) {
        return acc;
      }

      return {
        ...acc,
        [isin]: {
          isCheck: false,
          name: security[SECURITY_LABELS[SecuritiesIdentifiers.SecurityName]],
          isin,
          alias: security[SECURITY_LABELS[SecuritiesIdentifiers.Alias]],
        },
      };
    }, {});

    setSecuritiesInList(securitiesFormatted);
    setSecuritiesInListFiltered(securitiesFormatted);
  }, [securitiesFromState]);

  useEffect(() => {
    handleFilterSecurities(searchCriteria);
  }, [securitiesInList]);

  return (
    <AccordionItem disabled={!isConnectionEstablished} value={isConnectionEstablished ? AccordionValue.Table : null}>
      <AccordionHeader icon={<DocumentTable20Filled />}>
        <Body1>Securities</Body1>
      </AccordionHeader>

      <AccordionPanel>
        <div className={styles.root}>
          <Options
            onChangeSecuritiesIdentifiers={handleSecuritiesIdentifiersChange}
            addDataProducers={addDataProducers}
            deleteDataProducers={deleteDataProducers}
            onSearch={handleSearch}
          />

          <Divider />

          <SecuritiesList
            setSecuritiesInList={setSecuritiesInList}
            securitiesInList={securitiesInList}
            securitiesIdentifiers={securitiesIdentifiers}
            securitiesInListFiltered={securitiesInListFiltered}
          />
        </div>
      </AccordionPanel>
    </AccordionItem>
  );
};

export default Securities;
